import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
// import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
// import SideBar from "../try/SideBar";
import api from "../../services/api";
import BottomNavbar from "../../componets/BottomNavbar";
// import "../AdminDashboard.css";
import "./WhatsAppControlPage.css";

const WhatsAppControlPage = () => {
  const location = useLocation();
  const [qrCode, setQrCode] = useState(null);
  const [status, setStatus] = useState("initializing");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [queuedMessages, setQueuedMessages] = useState(0);
  const [qrFetchError, setQrFetchError] = useState(null);
  const [statusLogs, setStatusLogs] = useState([]);
  const startCalledRef = useRef(false);

  const [fileToSend, setFileToSend] = useState({ url: null, name: null });

  // Pre-fill when navigating from contacts, bills, or PDFs page
  useEffect(() => {
    const state = location.state;
    if (state?.contactNumber) {
      const digits = String(state.contactNumber).replace(/\D/g, "");
      const tenDigits = digits.length >= 10 ? digits.slice(-10) : digits;
      setPhoneNumber(tenDigits);
      if (state.contactName) {
        setMessage((prev) => (prev ? prev : `Hello ${state.contactName}`));
      }
    }
    if (state?.fileUrl != null || state?.fileName != null) {
      setFileToSend({ url: state.fileUrl || null, name: state.fileName || "file" });
      if (state.fileName && !message) {
        setMessage((prev) => (prev ? prev : state.fileName));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- message set inside effect, omit to avoid loop
  }, [location.state]);

  // Fetch QR code and status (backend routes: /qr, /qr/status)
  const fetchStatus = async () => {
    try {
      const res = await api.get("/qr/status");
      if (res.data?.success && res.data?.data) {
        setStatus(res.data.data.status || "initializing");
        setQueuedMessages(res.data.data.queuedMessages ?? 0);
        if (Array.isArray(res.data.data.logs)) {
          setStatusLogs(res.data.data.logs);
        }
      }
    } catch (error) {
      console.error("Error fetching status:", error);
    }
  };

  const fetchQR = async () => {
    try {
      setQrFetchError(null);
      const res = await api.get("/qr");
      if (res.data?.success && res.data?.qr) {
        setQrCode(res.data.qr);
      } else if (res.data?.clientReady) {
        setQrCode(null);
      } else {
        setQrCode(null);
        if (res.data?.success === false && res.data?.message) {
          setQrFetchError(res.data.message);
        }
      }
    } catch (error) {
      console.error("Error fetching QR:", error);
      setQrFetchError(error.response?.data?.message || "Failed to fetch QR");
      setQrCode(null);
    }
  };

  // Ensure WhatsApp client is running on page load (call /wp/start once)
  useEffect(() => {
    if (startCalledRef.current) return;
    startCalledRef.current = true;

    const ensureStarted = async () => {
      try {
        const res = await api.post("/wp/start");
        if (res.data?.success) {
          if (res.data?.alreadyRunning) {
            toast.info("WhatsApp client already running. Fetching QR...");
          } else {
            toast.info("WhatsApp client starting... QR will appear shortly.");
          }
        }
      } catch (e) {
        // Auth error or other – ignore on auto-start
      }
    };

    ensureStarted();
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchQR();

    const interval = setInterval(() => {
      fetchStatus();
      fetchQR();
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const handleStart = async () => {
    setLoading(true);
    try {
      const res = await api.post("/wp/start");
      if (res.data.success) {
        if (res.data.alreadyRunning) {
          toast.info(res.data.message || "WhatsApp client is already running.");
        } else {
          toast.success(res.data.message || "WhatsApp client started successfully.");
        }
        setTimeout(() => {
          fetchStatus();
          fetchQR();
        }, 1000);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to start client");
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    if (!window.confirm("Are you sure you want to stop the WhatsApp client?")) {
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/wp/stop");
      if (res.data.success) {
        toast.success("WhatsApp client stopped successfully");
        setQrCode(null);
        setTimeout(() => {
          fetchStatus();
        }, 500);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to stop client");
    } finally {
      setLoading(false);
    }
  };

  const handleReinitialize = async () => {
    if (!window.confirm("This will disconnect and reconnect WhatsApp. Continue?")) {
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/qr/reinitialize");
      if (res.data.success) {
        toast.info("Reinitializing... QR code will be available shortly");
        setTimeout(() => {
          fetchStatus();
          fetchQR();
        }, 1500);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to reinitialize");
    } finally {
      setLoading(false);
    }
  };

  const handleSendTest = async () => {
    if (!phoneNumber) {
      toast.warning("Please enter phone number");
      return;
    }
    if (!message && !fileToSend.url) {
      toast.warning("Please enter a message or attach a file");
      return;
    }

    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(phoneNumber)) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        number: phoneNumber,
        message: message || (fileToSend.name ? fileToSend.name : ""),
      };
      if (fileToSend.url) {
        payload.filePath = fileToSend.url.replace(/^\//, "");
      }
      const res = await api.post("/wp/send", payload);
      if (res.data.success) {
        toast.success(fileToSend.url ? "File sent successfully!" : "Message sent successfully!");
        setMessage("");
        if (fileToSend.url) setFileToSend({ url: null, name: null });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case "connected":
        return "#4CAF50";
      case "waiting_for_scan":
        return "#FF9800";
      case "initializing":
        return "#2196F3";
      default:
        return "#757575";
    }
  };

  return (
    <div className="admin-dashboard-container">
      {/* <Helmet>
        <title>WhatsApp Control - ORIT Admin</title>
      </Helmet> */}
      {/* <SideBar /> */}
      <div className="admin-dashboard-content">
        <div className="whatsapp-control-page">
          <div className="whatsapp-header">
            <h1>WhatsApp Control Panel</h1>
            <div className="status-indicator" style={{ backgroundColor: getStatusColor() }}>
              <span>{status.toUpperCase().replace("_", " ")}</span>
            </div>
          </div>

          <div className="whatsapp-controls">
            <div className="control-card">
              <h3>Connection Status</h3>
              <div className="status-info">
                <p>
                  <strong>Status:</strong> {status.replace("_", " ")}
                </p>
                {queuedMessages > 0 && (
                  <p>
                    <strong>Queued Messages:</strong> {queuedMessages}
                  </p>
                )}
              </div>
              <div className="button-group">
                <button
                  onClick={handleStart}
                  disabled={loading || status === "connected"}
                  className="btn btn-start"
                >
                  ▶️ Start
                </button>
                <button
                  onClick={handleStop}
                  disabled={loading || status !== "connected"}
                  className="btn btn-stop"
                >
                  ⏹️ Stop
                </button>
                <button
                  onClick={handleReinitialize}
                  disabled={loading}
                  className="btn btn-refresh"
                >
                  🔄 Reinitialize
                </button>
              </div>
            </div>

            {qrCode && status !== "connected" && (
              <div className="control-card">
                <h3>QR Code - Scan to Connect</h3>
                <div className="qr-container">
                  <img src={qrCode} alt="WhatsApp QR Code" />
                </div>
                <p className="qr-instructions">
                  Open WhatsApp on your phone → Settings → Linked Devices → Link a Device → Scan this QR code
                </p>
              </div>
            )}

            {!qrCode && status !== "connected" && (
              <div className="control-card">
                <h3>⏳ Waiting for QR Code</h3>
                <p className="qr-instructions">
                  {qrFetchError || "Initializing WhatsApp client... This usually takes 10–30 seconds."}
                </p>
                <p className="qr-instructions" style={{ marginTop: 8, fontSize: "0.9rem" }}>
                  If QR does not appear, click <strong>Start</strong> or <strong>Reinitialize</strong> above.
                </p>
              </div>
            )}

            {status === "connected" && (
              <div className="control-card">
                <h3>✅ Connected</h3>
                <p>WhatsApp is connected and ready to send messages.</p>
              </div>
            )}

            <div className="control-card">
              <h3>Status Log</h3>
              <div className="status-log-box">
                {statusLogs.length === 0 ? (
                  <p className="status-log-empty">No status messages yet. Start the client to see logs.</p>
                ) : (
                  statusLogs.map((line, i) => (
                    <div key={i} className="status-log-line">
                      {line}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="control-card">
              <h3>Send Message / File</h3>
              <div className="test-message-form">
                {fileToSend.url && (
                  <div className="form-group" style={{ padding: "8px 12px", background: "#e7f8ee", borderRadius: "8px", marginBottom: "12px" }}>
                    <strong>📎 Attached:</strong> {fileToSend.name || "file"}
                  </div>
                )}
                <div className="form-group">
                  <label>Phone Number (10 digits)</label>
                  <input
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="8469514209"
                    maxLength={10}
                  />
                </div>
                <div className="form-group">
                  <label>Message {fileToSend.url && "(optional with file)"}</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Enter your message here..."
                    rows={4}
                  />
                </div>
                <button
                  onClick={handleSendTest}
                  disabled={loading || status !== "connected" || !phoneNumber || (!message && !fileToSend.url)}
                  className="btn btn-send"
                >
                  {fileToSend.url ? "📤 Send File" : "📤 Send Message"}
                </button>
              </div>
            </div>
          </div>

          <footer className="whatsapp-page-footer">
            <p>WhatsApp Control Panel &copy; {new Date().getFullYear()}</p>
            <p className="footer-note">Link your device via QR code to send messages.</p>
          </footer>
        </div>
      </div>
      <BottomNavbar />
    </div>
  );
};

export default WhatsAppControlPage;
